import { useState } from 'react';
import { useMutation } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { Field } from '../../../components/Field.jsx';
import { REGISTRAR_INGRESO } from './gql.js';

const emptyItem = () => ({ descripcion: '', cantidad: 1, costoUnit: 0, productoId: '' });

const initialState = {
  sucursalId: '',
  proveedorId: '',
  ocId: '',
  fecha: new Date().toISOString().slice(0, 16),
  tipo: 'COMPRA',
  usuario: '',
  observacion: '',
};

export default function RecepcionForm({ onCancel, onSaved }) {
  const [formState, setFormState] = useState(initialState);
  const [items, setItems] = useState([emptyItem()]);
  const [errors, setErrors] = useState({});
  const [, registrarIngreso] = useMutation(REGISTRAR_INGRESO);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: field === 'descripcion' ? value : Number(value),
      };
      return next;
    });
  };

  const handleAddItem = () => setItems((prev) => [...prev, emptyItem()]);
  const handleRemoveItem = (index) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!formState.sucursalId) nextErrors.sucursalId = 'Indicá la sucursal de destino.';
    if (!formState.usuario.trim()) nextErrors.usuario = 'El usuario es obligatorio.';
    const validItems = items.filter((item) => item.descripcion.trim());
    if (!validItems.length) nextErrors.items = 'Agregá al menos un ítem.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    try {
      await registrarIngreso({
        input: {
          sucursalId: String(formState.sucursalId),
          proveedorId: formState.proveedorId ? String(formState.proveedorId) : null,
          ocId: formState.ocId ? String(formState.ocId) : null,
          fecha: formState.fecha,
          tipo: formState.tipo,
          observacion: formState.observacion.trim() || null,
          usuario: formState.usuario.trim(),
          items: validItems.map((item) => ({
            productoId: item.productoId ? String(item.productoId) : null,
            descripcion: item.descripcion.trim(),
            cantidad: Number(item.cantidad) || 0,
            costoUnit: Number(item.costoUnit) || 0,
          })),
        },
      });
      onSaved?.();
    } catch (error) {
      setErrors({ form: error.message });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Registrar ingreso de mercadería"
        subtitle="Generá el ingreso de stock para actualizar inventario y Kardex."
      />

      {errors.form ? (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{errors.form}</div>
      ) : null}

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          <Field label="Sucursal" hint={errors.sucursalId}>
            <input
              value={formState.sucursalId}
              onChange={(event) => setFormState((prev) => ({ ...prev, sucursalId: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Proveedor">
            <input
              value={formState.proveedorId}
              onChange={(event) => setFormState((prev) => ({ ...prev, proveedorId: event.target.value }))}
              placeholder="Opcional"
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Orden de compra">
            <input
              value={formState.ocId}
              onChange={(event) => setFormState((prev) => ({ ...prev, ocId: event.target.value }))}
              placeholder="Opcional"
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Fecha y hora">
            <input
              type="datetime-local"
              value={formState.fecha}
              onChange={(event) => setFormState((prev) => ({ ...prev, fecha: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Tipo">
            <select
              value={formState.tipo}
              onChange={(event) => setFormState((prev) => ({ ...prev, tipo: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="COMPRA">Compra</option>
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="AJUSTE">Ajuste</option>
              <option value="INVENTARIO">Inventario</option>
            </select>
          </Field>
          <Field label="Usuario" hint={errors.usuario}>
            <input
              value={formState.usuario}
              onChange={(event) => setFormState((prev) => ({ ...prev, usuario: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Observación">
            <textarea
              value={formState.observacion}
              onChange={(event) => setFormState((prev) => ({ ...prev, observacion: event.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Ítems</h2>
          <button
            type="button"
            onClick={handleAddItem}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-3 py-1.5 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Agregar ítem
          </button>
        </div>
        {errors.items ? <p className="text-sm text-red-300">{errors.items}</p> : null}
        <div className="flex flex-col gap-4">
          {items.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)] p-4">
              <div className="col-span-12 md:col-span-4">
                <label className="mb-1 block text-xs font-medium text-muted">Descripción</label>
                <input
                  value={item.descripcion}
                  onChange={(event) => handleItemChange(index, 'descripcion', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Cantidad</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.cantidad}
                  onChange={(event) => handleItemChange(index, 'cantidad', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Costo unitario</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.costoUnit}
                  onChange={(event) => handleItemChange(index, 'costoUnit', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Producto ID</label>
                <input
                  value={item.productoId}
                  onChange={(event) => handleItemChange(index, 'productoId', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-200 transition hover:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  disabled={items.length === 1}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Registrar ingreso
          </button>
        }
      />
    </form>
  );
}
